import React from "react"
import { Dialog, DialogContent, IconButton, Box, Stack, Button, useTheme, Divider } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import dayjs, { Dayjs } from "dayjs"
import { StatusOption, RequesterRecord, getRequesterName } from "./MyFilesFilter"
import { useResponsive } from "src/utils/useResponsive"

interface MyFilesFilterModalProps {
  open: boolean
  onClose: () => void
  statusFilter: StatusOption[]
  onStatusFilterChange: (statuses: StatusOption[]) => void
  statusOptions: StatusOption[]
  uploadDateFrom: Dayjs | null
  onUploadDateFromChange: (date: Dayjs | null) => void
  uploadDateTo: Dayjs | null
  onUploadDateToChange: (date: Dayjs | null) => void
  requesterList?: RequesterRecord[]
  requesterFilter?: RequesterRecord
  onRequesterFilterChange: (requester: RequesterRecord | undefined) => void
  onClearFilters: () => void
}

const MyFilesFilterModal: React.FC<MyFilesFilterModalProps> = ({
  open,
  onClose,
  statusFilter,
  onStatusFilterChange,
  statusOptions,
  uploadDateFrom,
  onUploadDateFromChange,
  uploadDateTo,
  onUploadDateToChange,
  requesterList,
  requesterFilter,
  onRequesterFilterChange,
  onClearFilters
}) => {
  const theme = useTheme()
  const { isMobile } = useResponsive()
  const handleApplyFilters = () => {
    onClose()
  }

  const handleClearAndClose = () => {
    onClearFilters()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      fullScreen={true}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "0",
          backgroundColor: "#FAFAFA"
        }
      }}
    >
      <DialogContent
        sx={{
          p: theme.spacing(10, 4, 3, 4),
          position: "relative"
        }}
      >
        <Stack spacing={3} alignItems="stretch">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box sx={{ fontSize: "1.5rem", fontWeight: 600 }}>Filters</Box>
            <Button variant="outlined" onClick={handleClearAndClose} color="secondary">
              Clear
            </Button>
          </Stack>
          <Divider />

          {/* Status Filter */}
          <Box>
            <CustomAutocomplete<StatusOption>
              value={statusFilter}
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(e, val) => {
                onStatusFilterChange(val as StatusOption[])
              }}
              label="Status"
              size="medium"
              multiple
            />
          </Box>

          {/* Upload Date From */}
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Upload date from"
                value={uploadDateFrom}
                onChange={onUploadDateFromChange}
                slotProps={{
                  textField: { fullWidth: true },
                  field: { clearable: true, onClear: () => onUploadDateFromChange(null) }
                }}
                maxDate={uploadDateTo ? uploadDateTo : dayjs()}
              />
            </LocalizationProvider>
          </Box>

          {/* Upload Date To */}
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Upload date to"
                value={uploadDateTo}
                onChange={onUploadDateToChange}
                slotProps={{
                  textField: { fullWidth: true },
                  field: { clearable: true, onClear: () => onUploadDateToChange(null) }
                }}
                minDate={uploadDateFrom ? uploadDateFrom : undefined}
                maxDate={dayjs()}
              />
            </LocalizationProvider>
          </Box>

          {/* Requester Filter */}
          {!isMobile && requesterList && (
            <Box>
              <CustomAutocomplete
                value={requesterFilter}
                options={requesterList}
                getOptionLabel={(option) => (option ? getRequesterName(requesterList, option.userId) : "")}
                onChange={(e, val) => onRequesterFilterChange(val as RequesterRecord | undefined)}
                label={"Requester"}
                isOptionEqualToValue={(o, v) => o?.userId === v?.userId}
                size="medium"
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Stack spacing={2} sx={{ mt: 4 }}>
            <Button variant="contained" onClick={handleApplyFilters}>
              Search
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default MyFilesFilterModal
